import React, { useEffect, useState } from "react";
import {
  getTailwindsAndHeadwinds,
  getTenantById,
} from "../../../../../shared/services/apis";
import IframeViewer from "../../../../../shared/ui/urlOpener/IFrameViewer";
import saveAs from "file-saver";
import { RiDownloadLine } from "react-icons/ri";
import autoTable from "jspdf-autotable";
import jsPDF from "jspdf";

// Type definitions
interface TailwindOrHeadwind {
  title: string;
  description: string;
  source: string;
}

interface TailwindsHeadwindsSectionProps {
  tenantId?: string;
  tenantServiceId?: string;
  tenantServiceOptionId?: string;
}
interface Tenant {
  id: string;
  name: string;
  logoUrl?: string;
}

const TailwindsHeadwindsSection: React.FC<TailwindsHeadwindsSectionProps> = ({
  tenantId,
  tenantServiceId,
  tenantServiceOptionId,
}: TailwindsHeadwindsSectionProps) => {
  const [tailwinds, setTailwinds] = useState<TailwindOrHeadwind[]>([]);
  const [headwinds, setHeadwinds] = useState<TailwindOrHeadwind[]>([]);
  const [loading, setLoading] = useState(true);
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingWord, setDownloadingWord] = useState(false);
  const [tenant, setTenant] = useState<Tenant | null>();

  useEffect(() => {
    if (!tenantId) return;

    const fetchTenant = async () => {
      try {
        const data = await getTenantById(tenantId);
        setTenant(data);
      } catch (error) {
        console.error("Error fetching tenant:", error);
      }
    };

    fetchTenant();
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId || !tenantServiceId || !tenantServiceOptionId) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await getTailwindsAndHeadwinds(
          tenantId,
          tenantServiceId,
          tenantServiceOptionId
        );
        setTailwinds(response.content.Tailwinds || []);
        setHeadwinds(response.content.Headwinds || []);
      } catch (err) {
        console.error("Failed to fetch data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [tenantId, tenantServiceId, tenantServiceOptionId]);
  const handleSourceClick = (e: React.MouseEvent, url: string) => {
    e.preventDefault();
    setIframeUrl(url); // open iframe on right side
  };

  // ====================== PDF DOWNLOAD ======================
  const downloadPDF = async () => {
    setDownloadingPdf(true);
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      // Watermark loader utility
      const loadImage = (url: string): Promise<HTMLImageElement> => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = "Anonymous";
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = url;
        });
      };

      // Load watermark image if logoUrl exists
      let watermarkImg: HTMLImageElement | null = null;
      if (tenant?.logoUrl) {
        try {
          watermarkImg = await loadImage(tenant.logoUrl);
        } catch (error) {
          console.warn("Failed to load watermark image:", error);
        }
      }

      // Draw watermark utility
      const addWatermark = (pageNumber: number) => {
        if (!watermarkImg) return;
        doc.setPage(pageNumber);
        doc.saveGraphicsState();
        doc.setGState(doc.GState({ opacity: 0.1 }));
        const watermarkWidth = 80;
        const watermarkHeight =
          (watermarkImg.height / watermarkImg.width) * watermarkWidth;
        const xPos = (pageWidth - watermarkWidth) / 2;
        const yPos = (pageHeight - watermarkHeight) / 2;
        doc.addImage(
          watermarkImg,
          "PNG",
          xPos,
          yPos,
          watermarkWidth,
          watermarkHeight
        );
        doc.restoreGraphicsState();
      };

      // Document header
      doc.setFontSize(18);
      doc.setFont("helvetica", "bold");
      doc.text("Tailwinds & Headwinds Report", pageWidth / 2, 20, {
        align: "center",
      });

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100, 100, 100);
      const currentDateTime = new Date().toLocaleString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
      doc.text(`Generated on ${currentDateTime}`, pageWidth / 2, 28, {
        align: "center",
      });

      let y = 40;

      // ====== Tailwinds Table ======
      if (tailwinds.length > 0) {
        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text("Tailwinds (Positive Factors)", 14, y);
        y += 8;
        const tailwindRows = tailwinds.map((t) => [
          t.title,
          t.description,
          t.source,
        ]);
        autoTable(doc, {
          startY: y,
          head: [["Title", "Description", "Source"]],
          body: tailwindRows,
          theme: "grid",
          styles: { fontSize: 9, cellPadding: 3 },
          columnStyles: {
            0: { cellWidth: 40 }, // Title - narrower
            1: { cellWidth: 100 }, // Description - wider
            2: { cellWidth: 40 }, // Source - narrower
          },
          headStyles: {
            fillColor: [214, 234, 248],
            textColor: [55, 65, 81],
            fontStyle: "bold",
          },
          margin: { left: 14, right: 14 },
        });
        y = (doc as any).lastAutoTable.finalY + 10;
      }

      // ====== Headwinds Table ======
      if (headwinds.length > 0) {
        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text("Headwinds (Negative Factors)", 14, y);
        y += 8;
        const headwindRows = headwinds.map((h) => [
          h.title,
          h.description,
          h.source,
        ]);
        autoTable(doc, {
          startY: y,
          head: [["Title", "Description", "Source"]],
          body: headwindRows,
          theme: "grid",
          styles: { fontSize: 9, cellPadding: 3 },
          columnStyles: {
            0: { cellWidth: 40 }, // Title - narrower
            1: { cellWidth: 100 }, // Description - wider
            2: { cellWidth: 40 }, // Source - narrower
          },
          headStyles: {
            fillColor: [250, 219, 216],
            textColor: [55, 65, 81],
            fontStyle: "bold",
          },
          margin: { left: 14, right: 14 },
        });
        y = (doc as any).lastAutoTable.finalY + 10;
      }

      // Add watermark to all pages
      const totalPages = doc.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        addWatermark(i);
      }

      // Footer on every page
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(
          `Confidential - Generated by ${tenant?.name || ""}`,
          pageWidth / 2,
          pageHeight - 16,
          { align: "center" }
        );
        doc.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 8, {
          align: "center",
        });
      }

      doc.save(`Tailwinds_Headwinds_${tenant?.name || "Report"}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Failed to generate PDF. Please try again.");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const downloadWord = async () => {
    setDownloadingWord(true);
    try {
      const {
        Document,
        Paragraph,
        ImageRun,
        TextRun,
        HeadingLevel,
        AlignmentType,
        Table,
        TableRow,
        TableCell,
        WidthType,
        Footer,
        Packer,
        convertInchesToTwip,
        PageOrientation,
      } = await import("docx");

      const base64ToUint8Array = (base64: string): Uint8Array => {
        const binaryString = window.atob(base64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes;
      };

      // Load logo image if logoUrl exists
      let logoImageData: {
        data: Uint8Array;
        mimeType: "jpg" | "png" | "gif" | "bmp";
      } | null = null;
      if (tenant?.logoUrl) {
        try {
          const response = await fetch(tenant.logoUrl);
          const blob = await response.blob();

          const getMimeType = (
            blobType: string
          ): "jpg" | "png" | "gif" | "bmp" => {
            if (blobType.includes("png")) return "png";
            if (blobType.includes("jpeg") || blobType.includes("jpg"))
              return "jpg";
            if (blobType.includes("gif")) return "gif";
            if (blobType.includes("bmp")) return "bmp";
            return "png";
          };

          const mimeType = getMimeType(blob.type);

          const base64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              const result = reader.result as string;
              const base64Data = result.split(",")[1];
              resolve(base64Data);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });

          const uint8Array = base64ToUint8Array(base64);
          logoImageData = { data: uint8Array, mimeType };
        } catch (error) {
          console.error("Failed to load logo image:", error);
        }
      }

      // Helper to make table rows
      const makeRows = (
        items: { title: string; description: string; source: string }[]
      ) =>
        items.map(
          (item) =>
            new TableRow({
              children: [
                new TableCell({
                  children: [new Paragraph(item.title)],
                }),
                new TableCell({
                  children: [new Paragraph(item.description)],
                }),
                new TableCell({
                  children: [new Paragraph(item.source)],
                }),
              ],
            })
        );

      // Tailwinds Table
      const tailwindsTable = new Table({
        rows: [
          new TableRow({
            children: [
              new TableCell({
                children: [new Paragraph("Title")],
                shading: { fill: "D6EAF8" },
              }),
              new TableCell({
                children: [new Paragraph("Description")],
                shading: { fill: "D6EAF8" },
              }),
              new TableCell({
                children: [new Paragraph("Source")],
                shading: { fill: "D6EAF8" },
              }),
            ],
          }),
          ...makeRows(tailwinds),
        ],
        width: { size: 100, type: WidthType.PERCENTAGE },
        columnWidths: [2000, 5000, 3000],
      });

      // Headwinds Table
      const headwindsTable = new Table({
        rows: [
          new TableRow({
            children: [
              new TableCell({
                children: [new Paragraph("Title")],
                shading: { fill: "FADBD8" },
              }),
              new TableCell({
                children: [new Paragraph("Description")],
                shading: { fill: "FADBD8" },
              }),
              new TableCell({
                children: [new Paragraph("Source")],
                shading: { fill: "FADBD8" },
              }),
            ],
          }),
          ...makeRows(headwinds),
        ],
        width: { size: 100, type: WidthType.PERCENTAGE },
        columnWidths: [2000, 5000, 3000],
      });

      // Footer: add text and image (if present)
      const footerChildren = [
        new Paragraph({
          children: [
            new TextRun({
              text: `Confidential - Generated by ${tenant?.name || ""}`,
              size: 10,
              color: "888888",
            }),
          ],
          alignment: AlignmentType.CENTER,
        }),
      ];

      if (logoImageData) {
        footerChildren.push(
          new Paragraph({
            children: [
              new ImageRun({
                type: logoImageData.mimeType,
                data: logoImageData.data,
                transformation: {
                  width: 60,
                  height: 60,
                },
              }),
            ],
            alignment: AlignmentType.CENTER,
          })
        );
      }

      const doc = new Document({
        creator: "Your App",
        title: "Tailwinds & Headwinds Report",
        sections: [
          {
            properties: {
              page: {
                margin: {
                  top: convertInchesToTwip(1),
                  right: convertInchesToTwip(0.75),
                  bottom: convertInchesToTwip(1),
                  left: convertInchesToTwip(0.75),
                },
                size: { orientation: PageOrientation.PORTRAIT },
              },
            },
            footers: {
              default: new Footer({
                children: footerChildren,
              }),
            },
            children: [
              new Paragraph({
                text: "Tailwinds & Headwinds Report",
                heading: HeadingLevel.HEADING_1,
                alignment: AlignmentType.CENTER,
              }),
              new Paragraph({
                text: `Generated on: ${new Date().toLocaleDateString()}`,
                alignment: AlignmentType.CENTER,
              }),
              new Paragraph({
                text: "Tailwinds (Positive Factors)",
                heading: HeadingLevel.HEADING_2,
              }),
              tailwindsTable,
              new Paragraph({
                text: "Headwinds (Negative Factors)",
                heading: HeadingLevel.HEADING_2,
              }),
              headwindsTable,
            ],
          },
        ],
      });

      // Download
      const blob = await Packer.toBlob(doc);
      saveAs(blob, `Tailwinds_Headwinds_${tenant?.name || "Report"}.docx`);
    } catch (error) {
      console.error("Error generating Word document:", error);
    } finally {
      setDownloadingWord(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-[#FDFDFD] rounded-2xl shadow border border-gray-200 max-w-4xl mt-10 2xl:mt-20 mx-auto grid grid-cols-1 md:grid-cols-2 h-[600px] overflow-y-auto mb-10 animate-pulse">
        {/* Tailwinds Skeleton */}
        <div>
          <div className="p-6 bg-[#FAFBFD] sticky top-0 z-10 border-b border-gray-200">
            <div className="h-6 bg-gray-200 rounded w-32 mb-2"></div>
            <div className="h-4 bg-gray-100 rounded w-52"></div>
          </div>
          {[...Array(3)].map((_, idx) => (
            <div key={idx} className="mb-6 px-6">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-gray-100 rounded w-full mb-2"></div>
              <div className="h-3 bg-gray-100 rounded w-5/6 mb-3"></div>
              <div className="h-5 bg-gray-200 rounded-full w-20"></div>
            </div>
          ))}
        </div>

        {/* Headwinds Skeleton */}
        <div className="border-t md:border-t-0 md:border-l border-gray-200">
          <div className="p-6 bg-[#FAFBFD] sticky top-0 z-10 border-b border-gray-200">
            <div className="h-6 bg-gray-200 rounded w-32 mb-2"></div>
            <div className="h-4 bg-gray-100 rounded w-52"></div>
          </div>
          {[...Array(3)].map((_, idx) => (
            <div key={idx} className="mb-6 px-6">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-gray-100 rounded w-full mb-2"></div>
              <div className="h-3 bg-gray-100 rounded w-5/6 mb-3"></div>
              <div className="h-5 bg-gray-200 rounded-full w-20"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#FDFDFD]  max-w-4xl  m-auto ">
      <div className="flex gap-2  justify-end">
        <button
          onClick={downloadPDF}
          disabled={downloadingPdf}
          className="flex items-center gap-2 hover:bg-gray-200 font-light px-4 py-2 rounded-full border border-gray-300 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
        >
          {downloadingPdf ? (
            <>
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Generating...
            </>
          ) : (
            <>
              <RiDownloadLine className="w-4 h-4" /> PDF
            </>
          )}
        </button>

        <button
          onClick={downloadWord}
          disabled={downloadingWord}
          className="flex items-center gap-2 hover:bg-gray-200 font-light px-4 py-2 rounded-full border border-gray-300 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
        >
          {downloadingWord ? (
            <>
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Generating...
            </>
          ) : (
            <>
              <RiDownloadLine className="w-4 h-4" />
              Word
            </>
          )}
        </button>
      </div>

      <div className="bg-[#FDFDFD] rounded-2xl shadow border border-gray-200 max-w-4xl  mt-10 2xl:mt-20 mx-auto grid grid-cols-1 md:grid-cols-2 ">
        {/* Tailwinds */}
        <div>
          <div className="p-6 bg-[#FAFBFD]  top-0 z-10 border-b border-gray-200 mb-6">
            <h2 className="text-2xl text-gray-800 mb-1">Tailwinds</h2>
            <p className="text-sm text-gray-500">
              Key catalysts for <span className="text-green-700">positive</span>{" "}
              growth scenario
            </p>
          </div>
          {tailwinds.map((item, idx) => {
            const url = new URL(item.source);
            const display = url.hostname.replace("www.", "");

            return (
              <div key={idx} className="mb-6 px-6">
                <h3 className="text-[15px] text-gray-700 mb-1">{item.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed mb-2">
                  {item.description}
                </p>
                <button
                  onClick={(e) => handleSourceClick(e, item.source)}
                  className="inline-block text-xs text-gray-700 bg-white border border-gray-300 px-2 py-0.5 rounded-full cursor-pointer hover:bg-gray-50 "
                >
                  {display}
                </button>
              </div>
            );
          })}
        </div>

        {/* Headwinds */}
        <div className="border-t md:border-t-0 md:border-l border-gray-200">
          <div className="p-6 bg-[#FAFBFD]  top-0 z-10 border-b border-gray-200 mb-6">
            <h2 className="text-2xl text-gray-800 mb-1">Headwinds</h2>
            <p className="text-sm text-gray-500">
              Key catalysts for <span className="text-red-400">negative</span>{" "}
              growth scenario
            </p>
          </div>
          {headwinds.map((item, idx) => {
            const url = new URL(item.source);
            const display = url.hostname.replace("www.", "");

            return (
              <div key={idx} className="mb-6 px-6">
                <h3 className="text-[15px] text-gray-700 mb-1">{item.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed mb-2">
                  {item.description}
                </p>
                <button
                  onClick={(e) => handleSourceClick(e, item.source)}
                  className="inline-block text-xs text-gray-700 bg-white border border-gray-300 px-2 py-0.5 rounded-full cursor-pointer hover:bg-gray-50"
                >
                  {display}
                </button>
              </div>
            );
          })}
        </div>
        {iframeUrl && (
          <IframeViewer url={iframeUrl} onClose={() => setIframeUrl(null)} />
        )}
      </div>
    </div>
  );
};

export default TailwindsHeadwindsSection;
